import { useState, useMemo, useRef, useEffect } from "react";
import { X } from "lucide-react";
import type { DraftCalendarItem, DraftCalendarSortField } from "@/types/draftCalendar";

// ─── Types ────────────────────────────────────────────────────────────────────

interface DateNode {
  year: number;
  months: { month: number; monthName: string; days: number[] }[];
}

export interface DateFilterDropdownProps {
  columnKey: DraftCalendarSortField;
  label: string;
  allItems: DraftCalendarItem[];
  selected: string[];
  onApply: (values: string[]) => void;
  onClear: () => void;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function toValidDate(raw: unknown): Date | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const date = raw instanceof Date ? raw : new Date(String(raw));
  return Number.isNaN(date.getTime()) ? null : date;
}

function buildDateTree(items: DraftCalendarItem[], columnKey: DraftCalendarSortField): DateNode[] {
  const map = new Map<number, Map<number, Set<number>>>();
  items.forEach((item) => {
    const raw = (item as unknown as Record<string, unknown>)[columnKey];
    const d = toValidDate(raw);
    if (!d) return;

    const y = d.getFullYear();
    const m = d.getMonth();
    const day = d.getDate();

    let monthMap = map.get(y);
    if (!monthMap) {
      monthMap = new Map<number, Set<number>>();
      map.set(y, monthMap);
    }

    let daySet = monthMap.get(m);
    if (!daySet) {
      daySet = new Set<number>();
      monthMap.set(m, daySet);
    }

    daySet.add(day);
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => a - b)
    .map(([year, months]) => ({
      year,
      months: Array.from(months.entries())
        .sort(([a], [b]) => a - b)
        .map(([month, days]) => ({
          month,
          monthName: MONTH_NAMES[month],
          days: Array.from(days).sort((a, b) => a - b),
        })),
    }));
}

/** Build a unique key for each date leaf to track selection */
function dateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Get all date keys for the entire tree */
function allDateKeys(tree: DateNode[]): string[] {
  const keys: string[] = [];
  tree.forEach((yn) =>
    yn.months.forEach((mn) =>
      mn.days.forEach((d) => keys.push(dateKey(yn.year, mn.month, d)))
    )
  );
  return keys;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DateFilterDropdown({
  columnKey,
  label,
  allItems,
  selected,
  onApply,
  onClear,
  onClose,
}: DateFilterDropdownProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const tree = useMemo(() => buildDateTree(allItems, columnKey), [allItems, columnKey]);
  const allKeys = useMemo(() => allDateKeys(tree), [tree]); 

  const [localSelected, setLocalSelected] = useState<Set<string>>(() => {
    if (selected.length > 0) return new Set(selected);
    return new Set(allKeys);
  });
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const allChecked = localSelected.size === allKeys.length;
  const noneChecked = localSelected.size === 0;

  // Close on outside click
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [onClose]);

  function toggleExpand(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleAll() {
    setLocalSelected(allChecked ? new Set() : new Set(allKeys));
  }

  function toggleYear(year: number) {
    const node = tree.find((n) => n.year === year);
    if (!node) return;
    const yearKeys = node.months.flatMap((mn) =>
      mn.days.map((d) => dateKey(year, mn.month, d))
    );
    const allYearSelected = yearKeys.every((k) => localSelected.has(k));
    setLocalSelected((prev) => {
      const next = new Set(prev);
      yearKeys.forEach((k) => (allYearSelected ? next.delete(k) : next.add(k)));
      return next;
    });
  }

  function toggleMonth(year: number, month: number) {
    const node = tree.find((n) => n.year === year);
    const mn = node?.months.find((m) => m.month === month);
    if (!mn) return;
    const monthKeys = mn.days.map((d) => dateKey(year, month, d));
    const allMonthSelected = monthKeys.every((k) => localSelected.has(k));
    setLocalSelected((prev) => {
      const next = new Set(prev);
      monthKeys.forEach((k) => (allMonthSelected ? next.delete(k) : next.add(k)));
      return next;
    });
  }

  function toggleDay(year: number, month: number, day: number) {
    const key = dateKey(year, month, day);
    setLocalSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function isYearChecked(year: number): boolean {
    const node = tree.find((n) => n.year === year);
    if (!node) return false;
    return node.months.every((mn) =>
      mn.days.every((d) => localSelected.has(dateKey(year, mn.month, d)))
    );
  }

  function isYearIndeterminate(year: number): boolean {
    const node = tree.find((n) => n.year === year);
    if (!node) return false;
    const yearKeys = node.months.flatMap((mn) =>
      mn.days.map((d) => dateKey(year, mn.month, d))
    );
    const count = yearKeys.filter((k) => localSelected.has(k)).length;
    return count > 0 && count < yearKeys.length;
  }

  function isMonthChecked(year: number, month: number): boolean {
    const node = tree.find((n) => n.year === year);
    const mn = node?.months.find((m) => m.month === month);
    if (!mn) return false;
    return mn.days.every((d) => localSelected.has(dateKey(year, month, d)));
  }

  function isMonthIndeterminate(year: number, month: number): boolean {
    const node = tree.find((n) => n.year === year);
    const mn = node?.months.find((m) => m.month === month);
    if (!mn) return false;
    const count = mn.days.filter((d) => localSelected.has(dateKey(year, month, d))).length;
    return count > 0 && count < mn.days.length;
  }

  function handleApply() {
    if (localSelected.size === allKeys.length) {
      onClear();
    } else {
      onApply(Array.from(localSelected));
    }
    onClose();
  }

  function handleClear() {
    onClear();
    onClose();
  }

  return (
    <div
      ref={panelRef}
      className="dcf-filter-panel dcf-date-filter-panel"
      role="dialog"
      aria-label={`Filter by ${label}`}
    >
      <div className="dcf-filter-header">
        <span className="dcf-filter-title">Filter: {label}</span>
        <button type="button" className="dcf-filter-close" onClick={onClose} aria-label="Close filter">
          <X size={13} />
        </button>
      </div>

      {/* Select All */}
      <div className="dcf-filter-select-all">
        <label className="dcf-filter-option">
          <input
            type="checkbox"
            checked={allChecked}
            ref={(el) => {
              if (el) el.indeterminate = !allChecked && !noneChecked;
            }}
            onChange={toggleAll}
            className="dcf-filter-checkbox"
          />
          <span>(Select All)</span>
        </label>
      </div>

      {/* Tree list */}
      <div className="dcf-filter-list dcf-date-filter-list">
        {tree.map((yearNode) => (
          <div key={yearNode.year} className="dcf-date-tree-node">
            {/* Year row */}
            <div className="dcf-date-tree-row">
              <button
                type="button"
                className="dcf-date-expand-btn"
                onClick={() => toggleExpand(`y-${yearNode.year}`)}
                aria-expanded={expanded.has(`y-${yearNode.year}`)}
                aria-label={`Expand ${yearNode.year}`}
              >
                {expanded.has(`y-${yearNode.year}`) ? "▾" : "▸"}
              </button>
              <label className="dcf-filter-option">
                <input
                  type="checkbox"
                  checked={isYearChecked(yearNode.year)}
                  ref={(el) => {
                    if (el) el.indeterminate = isYearIndeterminate(yearNode.year);
                  }}
                  onChange={() => toggleYear(yearNode.year)}
                  className="dcf-filter-checkbox"
                />
                <span>{yearNode.year}</span>
              </label>
            </div>

            {/* Months under year */}
            {expanded.has(`y-${yearNode.year}`) && (
              <div className="dcf-date-tree-children">
                {yearNode.months.map((mn) => (
                  <div key={mn.month} className="dcf-date-tree-node">
                    {/* Month row */}
                    <div className="dcf-date-tree-row">
                      <button
                        type="button"
                        className="dcf-date-expand-btn"
                        onClick={() => toggleExpand(`m-${yearNode.year}-${mn.month}`)}
                        aria-expanded={expanded.has(`m-${yearNode.year}-${mn.month}`)}
                        aria-label={`Expand ${mn.monthName}`}
                      >
                        {expanded.has(`m-${yearNode.year}-${mn.month}`) ? "▾" : "▸"}
                      </button>
                      <label className="dcf-filter-option">
                        <input
                          type="checkbox"
                          checked={isMonthChecked(yearNode.year, mn.month)}
                          ref={(el) => {
                            if (el) el.indeterminate = isMonthIndeterminate(yearNode.year, mn.month);
                          }}
                          onChange={() => toggleMonth(yearNode.year, mn.month)}
                          className="dcf-filter-checkbox"
                        />
                        <span>{mn.monthName}</span>
                      </label>
                    </div>

                    {/* Days under month */}
                    {expanded.has(`m-${yearNode.year}-${mn.month}`) && (
                      <div className="dcf-date-tree-children">
                        {mn.days.map((day) => (
                          <label key={day} className="dcf-filter-option dcf-date-day-option">
                            <input
                              type="checkbox"
                              checked={localSelected.has(dateKey(yearNode.year, mn.month, day))}
                              onChange={() => toggleDay(yearNode.year, mn.month, day)}
                              className="dcf-filter-checkbox"
                            />
                            <span>{day}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="dcf-filter-footer">
        <button type="button" className="dcf-filter-clear" onClick={handleClear}>
          Clear
        </button>
        <button type="button" className="dcf-filter-apply" onClick={handleApply}>
          OK
        </button>
      </div>
    </div>
  );
}
